/**
 * How the GiviSnaps feed is divided: what is new to you, and what you have
 * already seen, with a "You're all caught up" line between.
 *
 * Its own module, and types only, for the same reason chatGroups is: the
 * screen reaches Firebase through ../data, and this is the part that is pure
 * and can be held to a test on its own.
 *
 * The feed used to hold only what you had opened. Something new showed up as a
 * ring on a face in the rail and nowhere else, and it joined the feed under a
 * heading that said OPENED only once you had tapped it -- so the thing you came
 * to see was the one thing the feed never showed.
 */
import type { DailyPostDoc } from '../../shared/types';

interface Post {
  id: string;
  data: DailyPostDoc;
}

/** A GiviSnap you have not seen, which never includes your own. */
export function isUnseenBy(post: Post, uid: string): boolean {
  return post.data.authorUid !== uid && !(post.data.seenBy ?? []).includes(uid);
}

/** Only the prompted ones: a story lives on the camera tab, not in this feed. */
export function isGiviSnap(post: Post): boolean {
  return post.data.kind !== 'extra';
}

function when(post: Post): number {
  return (post.data.createdAt as { toMillis?: () => number } | null)?.toMillis?.() ?? 0;
}

/**
 * Split the feed at the line.
 *
 * `fresh` is which posts count as new for this visit, decided by the caller
 * and held still while the screen is open. It is not recomputed from seenBy on
 * every render because seeing a card is what writes seenBy -- so a card would
 * drop below the line the moment it had been read, pulling the feed out from
 * under the thumb that was reading it. The chat's "New messages" line is held
 * still for the same reason.
 *
 * Both halves newest first, which is what a feed is.
 */
export function splitFeed<T extends Post>(
  posts: T[],
  fresh: ReadonlySet<string>,
): { fresh: T[]; seen: T[] } {
  const ordered = [...posts].sort((a, b) => when(b) - when(a));
  return {
    fresh: ordered.filter((post) => fresh.has(post.id)),
    seen: ordered.filter((post) => !fresh.has(post.id)),
  };
}
